import { normalizeRhythmPattern, normalizeTokenArray } from '../../core/music/tokens';
import { cleanCompletarPrintTitle } from '../../core/print/printCompletarFormat';
import { repairCompletarText } from '../../core/print/completarBlankRepair';
import { applyStimulusFields } from '../../core/print/blockStimulus';
import type { GeometryPayload } from '../../core/types/mathGeometry';
import type { MusicGraphicMode, RhythmReadingMethod } from '../../core/types/music';
import type { RoutingMode } from '../../core/types/room.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';

const BLANK_RE = /\[([^\]]+)\]/g;

/** Answer tokens that mean "blank not resolved" — never valid student answers. */
const UNRESOLVED_BLANK_ANSWER_RE =
  /^(_{2,}|\.{3,}|…+|blank|espacio|respuesta|valor\s+que\s+falta|xxx+)$/i;

/**
 * True when a completar answer is still a UI placeholder (e.g. "___"), not real content.
 */
export function isUnresolvedCompletarBlankAnswer(answer: string): boolean {
  const token = String(answer || '').trim();
  if (!token) return true;
  if (UNRESOLVED_BLANK_ANSWER_RE.test(token)) return true;
  // Bracket-only leftovers like "___ ." after noise trim
  if (/^_+$/.test(token.replace(/\s+/g, ''))) return true;
  return false;
}

export function parseCompletarBlanks(text: string): Array<{ id: string; answer: string }> {
  const blanks: Array<{ id: string; answer: string }> = [];
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = BLANK_RE.exec(text)) !== null) {
    blanks.push({ id: `b${i + 1}`, answer: m[1]!.trim() });
    i += 1;
  }
  return blanks;
}

function normalize(s: string): string {
  return s
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function isBlankCorrect(blank: CompletarPayload['blanks'][number], value: string): boolean {
  const v = normalize(value);
  if (!v) return false;
  const accepted = [blank.answer, ...(blank.accept ?? [])].map(normalize);
  return accepted.includes(v);
}

function scoreResponse(payload: CompletarPayload, answers: Record<string, string>): number {
  let correct = 0;
  for (const blank of payload.blanks) {
    if (isBlankCorrect(blank, answers[blank.id] ?? '')) correct += 1;
  }
  return correct;
}

export function validateCompletarPayload(raw: unknown): CompletarPayload {
  if (!raw || typeof raw !== 'object') throw new Error('Completar payload must be an object');
  const data = raw as Partial<CompletarPayload>;
  if (!data.title) throw new Error('Completar requires title');
  const title = String(data.title);
  let text = repairCompletarText(String(data.text || ''), {
    title,
    printTitle: data.printTitle ? String(data.printTitle) : undefined,
  });
  const usesBlankToken = /\[BLANK\]/i.test(text);
  let blanks = Array.isArray(data.blanks) ? data.blanks : parseCompletarBlanks(text);
  if (usesBlankToken && blanks.length < 1) {
    throw new Error('Completar with [BLANK] requires blanks array with answers');
  }
  blanks = blanks.map((b, i) => ({
    id: String(b.id || `b${i + 1}`),
    answer: String(b.answer || '').trim(),
    accept: Array.isArray(b.accept) ? b.accept.map(String) : undefined,
  }));
  if (blanks.length < 1) throw new Error('Completar requires at least one blank [word]');
  if (blanks.some((b) => isUnresolvedCompletarBlankAnswer(b.answer))) {
    throw new Error('Completar blank answer is still an unresolved placeholder');
  }
  const inputMode = data.inputMode === 'drag' ? 'drag' : 'keyboard';
  const distractors = Array.isArray(data.distractors) ? data.distractors.map(String) : [];
  const chipPool = Array.isArray(data.chipPool) ? data.chipPool.map(String) : undefined;
  const referenceExpression = data.referenceExpression ? String(data.referenceExpression).trim() : undefined;
  const context = data.context ? String(data.context).trim() : undefined;
  const formulaLayout = data.formulaLayout === 'fraction-percent' ? 'fraction-percent' : undefined;
  const formulaNumeratorLabel =
    formulaLayout === 'fraction-percent'
      ? String(data.formulaNumeratorLabel || 'Valor Final − Valor Inicial').trim()
      : data.formulaNumeratorLabel
        ? String(data.formulaNumeratorLabel).trim()
        : undefined;
  const formulaDenominatorLabel =
    formulaLayout === 'fraction-percent'
      ? String(data.formulaDenominatorLabel || 'Valor Inicial').trim()
      : data.formulaDenominatorLabel
        ? String(data.formulaDenominatorLabel).trim()
        : undefined;
  const formulaSuffix =
    formulaLayout === 'fraction-percent'
      ? String(data.formulaSuffix || '× 100').trim()
      : data.formulaSuffix
        ? String(data.formulaSuffix).trim()
        : undefined;
  const rhythmPattern = normalizeRhythmPattern(data.rhythmPattern);
  const rhythmMethod =
    data.rhythmMethod === 'takadimi' || data.rhythmMethod === 'kodaly'
      ? data.rhythmMethod
      : data.rhythmMethod === 'tradicional'
        ? 'tradicional'
        : undefined;
  const graphicMode =
    data.graphicMode === 'symbols-labels' ? 'symbols-labels' : data.graphicMode === 'symbols' ? 'symbols' : undefined;
  const geometry =
    data.geometry && typeof data.geometry === 'object' && (data.geometry as GeometryPayload).shapeType
      ? (data.geometry as GeometryPayload)
      : undefined;

  return applyStimulusFields(
    {
      title: String(data.title),
      printTitle: data.printTitle ? cleanCompletarPrintTitle(String(data.printTitle)) : undefined,
      text,
      blanks,
      inputMode,
      referenceExpression,
      context,
      formulaLayout,
      formulaNumeratorLabel,
      formulaDenominatorLabel,
      formulaSuffix,
      distractors,
      chipPool,
      ...(geometry ? { geometry } : {}),
      ...(rhythmPattern ? { rhythmPattern } : {}),
      ...(rhythmMethod ? { rhythmMethod } : {}),
      ...(graphicMode ? { graphicMode } : {}),
    },
    raw,
    'completar',
  );
}

export function createInitialCompletarRuntime(_payload: CompletarPayload, _routing: RoutingMode): CompletarRuntime {
  return { responses: {}, finalized: {}, aggregates: { submitted: 0, correct: 0 } };
}

export const completarLogic: ActivityLogicPlugin<'completar'> = {
  mode: 'completar',
  validatePayload: validateCompletarPayload,
  createInitialRuntime: createInitialCompletarRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope, participant) {
    if (input.action === 'FILL') {
      const data = input.data as { blankId: string; value: string };
      const prev = ctx.runtime.responses[participant.participantId] ?? {};
      const responses = {
        ...ctx.runtime.responses,
        [participant.participantId]: { ...prev, [data.blankId]: String(data.value ?? '') },
      };
      return { runtime: { ...ctx.runtime, responses } };
    }
    if (input.action === 'SUBMIT') {
      const answers = ctx.runtime.responses[participant.participantId] ?? {};
      const correct = scoreResponse(ctx.payload, answers);
      const finalized = { ...ctx.runtime.finalized, [participant.participantId]: true };
      const all = Object.keys(finalized).filter((k) => finalized[k]);
      const totalCorrect = all.reduce((sum, pid) => {
        return sum + scoreResponse(ctx.payload, ctx.runtime.responses[pid] ?? {});
      }, 0);
      return {
        runtime: {
          ...ctx.runtime,
          finalized,
          aggregates: { submitted: all.length, correct: totalCorrect },
        },
        scores: [{ participantId: participant.participantId, delta: correct, reason: 'completar' }],
      };
    }
    return { runtime: ctx.runtime, rejected: true, rejectReason: 'UNKNOWN_ACTION' };
  },

  onHostAction(ctx, _action: HostActionPayload) {
    return { runtime: ctx.runtime };
  },
};

export function demoCompletarPayload(): CompletarPayload {
  // Neutro por asignatura — no biología (fuga vía defaultPayloadForMode).
  return validateCompletarPayload({
    title: 'Completa la idea',
    text: 'El concepto central del tema se llama [idea].',
    inputMode: 'keyboard',
    distractors: ['detalle', 'ejemplo'],
  });
}
